import { useState } from 'react'
import { UserCheck, ChevronDown, Mail, Bell, Clock, AlertTriangle, Eye, Pencil } from 'lucide-react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'

export type ReviewAction = 'approve' | 'reject' | 'request_changes' | 'custom'

export interface HumanReviewNodeConfig {
  reviewTitle: string
  reviewInstructions: string
  assigneeType: 'specific_user' | 'role' | 'workflow_owner' | 'custom_expression'
  assigneeValue: string
  allowedActions: ReviewAction[]
  customActions: string[]
  notifyViaEmail: boolean
  notifyViaInApp: boolean
  escalationEnabled: boolean
  escalationTimeoutHours: number
  escalationAssignee: string
  requireComment: boolean
  showPreviousStepOutput: boolean
  timeoutAction: 'approve' | 'reject' | 'escalate' | 'none'
  outputsToShow: string[]
  allowEdit: boolean
  editableFields: string[]
}

interface HumanReviewNodeProps {
  config: HumanReviewNodeConfig
  onChange: (config: HumanReviewNodeConfig) => void
  availableUsers: Array<{ id: string; name: string; email: string }>
  availableRoles: Array<{ id: string; name: string }>
  availableVariables: string[]
}

const defaultConfig: HumanReviewNodeConfig = {
  reviewTitle: 'Review Required',
  reviewInstructions: '',
  assigneeType: 'workflow_owner',
  assigneeValue: '',
  allowedActions: ['approve', 'reject'],
  customActions: [],
  notifyViaEmail: true,
  notifyViaInApp: true,
  escalationEnabled: false,
  escalationTimeoutHours: 24,
  escalationAssignee: '',
  requireComment: false,
  showPreviousStepOutput: true,
  timeoutAction: 'none',
  outputsToShow: [],
  allowEdit: false,
  editableFields: [],
}

const actionLabels: Record<ReviewAction, string> = {
  approve: 'Approve',
  reject: 'Reject',
  request_changes: 'Request Changes',
  custom: 'Custom Action',
}

const actionColors: Record<ReviewAction, string> = {
  approve: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  reject: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  request_changes: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  custom: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
}

export function HumanReviewNode({
  config: providedConfig,
  onChange,
  availableUsers,
  availableRoles,
  availableVariables
}: HumanReviewNodeProps) {
  // Merge with defaults to ensure all properties are defined
  const config = { ...defaultConfig, ...providedConfig }
  const [showEscalation, setShowEscalation] = useState(config.escalationEnabled)

  const toggleAction = (action: ReviewAction) => {
    const currentActions = config.allowedActions ?? []
    if (currentActions.includes(action)) {
      if (currentActions.length > 1) {
        onChange({ ...config, allowedActions: currentActions.filter(a => a !== action) })
      }
    } else {
      onChange({ ...config, allowedActions: [...currentActions, action] })
    }
  }

  return (
    <div className="space-y-5">
      {/* Review Title */}
      <div className="space-y-2">
        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Review Title
        </Label>
        <input
          type="text"
          value={config.reviewTitle}
          onChange={(e) => onChange({ ...config, reviewTitle: e.target.value })}
          placeholder="Enter a title for this review step"
          className="w-full h-11 px-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-white focus:border-transparent transition-all"
        />
      </div>

      {/* Review Instructions */}
      <div className="space-y-2">
        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Instructions for Reviewer
        </Label>
        <Textarea
          value={config.reviewInstructions}
          onChange={(e) => onChange({ ...config, reviewInstructions: e.target.value })}
          placeholder="Provide clear instructions on what the reviewer should check..."
          className="min-h-[80px] rounded-xl resize-none"
        />
        {availableVariables.length > 0 && (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Use {'{{variable_name}}'} to include data from previous steps
          </p>
        )}
      </div>

      {/* Assignee Configuration */}
      <div className="space-y-2">
        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
          <div className="flex items-center gap-2">
            <UserCheck className="h-4 w-4 text-blue-500" />
            Assign Review To
          </div>
        </Label>
        <Select
          value={config.assigneeType}
          onValueChange={(value: HumanReviewNodeConfig['assigneeType']) =>
            onChange({ ...config, assigneeType: value, assigneeValue: '' })
          }
        >
          <SelectTrigger className="w-full rounded-xl">
            <SelectValue placeholder="Select assignee type..." />
          </SelectTrigger>
          <SelectContent className="rounded-xl">
            <SelectItem value="workflow_owner" className="rounded-lg">
              Workflow Owner
            </SelectItem>
            <SelectItem value="specific_user" className="rounded-lg">
              Specific User
            </SelectItem>
            <SelectItem value="role" className="rounded-lg">
              Role (any member)
            </SelectItem>
            <SelectItem value="custom_expression" className="rounded-lg">
              Custom Expression
            </SelectItem>
          </SelectContent>
        </Select>

        {config.assigneeType === 'specific_user' && (
          <Select
            value={config.assigneeValue}
            onValueChange={(value) => onChange({ ...config, assigneeValue: value })}
          >
            <SelectTrigger className="w-full rounded-xl">
              <SelectValue placeholder="Select user..." />
            </SelectTrigger>
            <SelectContent className="rounded-xl">
              {availableUsers.map((user) => (
                <SelectItem key={user.id} value={user.id} className="rounded-lg">
                  {user.name} ({user.email})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {config.assigneeType === 'role' && (
          <Select
            value={config.assigneeValue}
            onValueChange={(value) => onChange({ ...config, assigneeValue: value })}
          >
            <SelectTrigger className="w-full rounded-xl">
              <SelectValue placeholder="Select role..." />
            </SelectTrigger>
            <SelectContent className="rounded-xl">
              {availableRoles.map((role) => (
                <SelectItem key={role.id} value={role.id} className="rounded-lg">
                  {role.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {config.assigneeType === 'custom_expression' && (
          <input
            type="text"
            value={config.assigneeValue}
            onChange={(e) => onChange({ ...config, assigneeValue: e.target.value })}
            placeholder="{{form.manager_email}}"
            className="w-full h-11 px-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-white focus:border-transparent transition-all"
          />
        )}
      </div>

      {/* Allowed Actions */}
      <div className="space-y-2">
        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Allowed Actions
        </Label>
        <div className="flex flex-wrap gap-2">
          {(Object.keys(actionLabels) as ReviewAction[]).filter(a => a !== 'custom').map((action) => (
            <button
              key={action}
              onClick={() => toggleAction(action)}
              className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                (config.allowedActions ?? []).includes(action)
                  ? actionColors[action]
                  : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-500 opacity-50'
              }`}
            >
              {actionLabels[action]}
            </button>
          ))}
        </div>
      </div>

      {/* Notification Settings */}
      <div className="space-y-3">
        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Notifications
        </Label>
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between p-3 rounded-xl bg-gray-50 dark:bg-gray-800/50">
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-gray-500" />
              <span className="text-sm text-gray-700 dark:text-gray-300">Email notification</span>
            </div>
            <Switch
              checked={config.notifyViaEmail}
              onCheckedChange={(checked) => onChange({ ...config, notifyViaEmail: checked })}
            />
          </div>
          <div className="flex items-center justify-between p-3 rounded-xl bg-gray-50 dark:bg-gray-800/50">
            <div className="flex items-center gap-2">
              <Bell className="h-4 w-4 text-gray-500" />
              <span className="text-sm text-gray-700 dark:text-gray-300">In-app notification</span>
            </div>
            <Switch
              checked={config.notifyViaInApp}
              onCheckedChange={(checked) => onChange({ ...config, notifyViaInApp: checked })}
            />
          </div>
        </div>
      </div>

      {/* Outputs to Show */}
      {availableVariables.length > 0 && (
        <div className="space-y-3">
          <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            <div className="flex items-center gap-2">
              <Eye className="h-4 w-4 text-blue-500" />
              Outputs to Show
            </div>
          </Label>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Select which variables to display to the reviewer
          </p>
          <div className="p-3 rounded-xl bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 space-y-2">
            {availableVariables.map((variable) => (
              <label
                key={variable}
                className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
              >
                <input
                  type="checkbox"
                  checked={config.outputsToShow.includes(variable)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      onChange({ ...config, outputsToShow: [...config.outputsToShow, variable] })
                    } else {
                      onChange({ ...config, outputsToShow: config.outputsToShow.filter(v => v !== variable) })
                    }
                  }}
                  className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-purple-600 focus:ring-purple-500"
                />
                <span className="font-mono text-sm text-purple-600 dark:text-purple-400">
                  {`{{${variable}}}`}
                </span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Allow Edit */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              <div className="flex items-center gap-2">
                <Pencil className="h-4 w-4 text-amber-500" />
                Allow Editing
              </div>
            </Label>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Allow reviewer to edit content before approval
            </p>
          </div>
          <Switch
            checked={config.allowEdit}
            onCheckedChange={(checked) => onChange({ ...config, allowEdit: checked, editableFields: checked ? config.editableFields : [] })}
          />
        </div>

        {config.allowEdit && availableVariables.length > 0 && (
          <div className="pl-4 border-l-2 border-amber-200 dark:border-amber-800 space-y-2">
            <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Editable Fields
            </Label>
            <div className="p-3 rounded-xl bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 space-y-2">
              {availableVariables.map((variable) => (
                <label
                  key={variable}
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={config.editableFields.includes(variable)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        onChange({ ...config, editableFields: [...config.editableFields, variable] })
                      } else {
                        onChange({ ...config, editableFields: config.editableFields.filter(v => v !== variable) })
                      }
                    }}
                    className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-amber-600 focus:ring-amber-500"
                  />
                  <span className="font-mono text-sm text-amber-600 dark:text-amber-400">
                    {`{{${variable}}}`}
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Additional Options */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Require Comment
            </Label>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Reviewer must provide a comment with their decision
            </p>
          </div>
          <Switch
            checked={config.requireComment}
            onCheckedChange={(checked) => onChange({ ...config, requireComment: checked })}
          />
        </div>
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Show Previous Output
            </Label>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Display output from the previous step to the reviewer
            </p>
          </div>
          <Switch
            checked={config.showPreviousStepOutput}
            onCheckedChange={(checked) => onChange({ ...config, showPreviousStepOutput: checked })}
          />
        </div>
      </div>

      {/* Escalation Settings */}
      <button
        onClick={() => {
          setShowEscalation(!showEscalation)
          if (!showEscalation) {
            onChange({ ...config, escalationEnabled: true })
          }
        }}
        className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 transition-colors"
      >
        <AlertTriangle className="h-4 w-4" />
        Escalation Settings
        <ChevronDown className={`h-4 w-4 transition-transform ${showEscalation ? 'rotate-180' : ''}`} />
      </button>

      {showEscalation && (
        <div className="space-y-4 pl-4 border-l-2 border-amber-200 dark:border-amber-800">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Enable Escalation
              </Label>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Escalate if not reviewed within timeout
              </p>
            </div>
            <Switch
              checked={config.escalationEnabled}
              onCheckedChange={(checked) => onChange({ ...config, escalationEnabled: checked })}
            />
          </div>

          {config.escalationEnabled && (
            <>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Timeout (hours)
                  </div>
                </Label>
                <input
                  type="number"
                  min={1}
                  max={168}
                  value={config.escalationTimeoutHours}
                  onChange={(e) => onChange({ ...config, escalationTimeoutHours: parseInt(e.target.value) || 24 })}
                  className="w-full h-11 px-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-white focus:border-transparent transition-all"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Escalate To
                </Label>
                <Select
                  value={config.escalationAssignee}
                  onValueChange={(value) => onChange({ ...config, escalationAssignee: value })}
                >
                  <SelectTrigger className="w-full rounded-xl">
                    <SelectValue placeholder="Select escalation target..." />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl">
                    {availableUsers.map((user) => (
                      <SelectItem key={user.id} value={user.id} className="rounded-lg">
                        {user.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Timeout Action (if escalation fails)
                </Label>
                <Select
                  value={config.timeoutAction}
                  onValueChange={(value: HumanReviewNodeConfig['timeoutAction']) =>
                    onChange({ ...config, timeoutAction: value })
                  }
                >
                  <SelectTrigger className="w-full rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl">
                    <SelectItem value="none" className="rounded-lg">
                      Wait indefinitely
                    </SelectItem>
                    <SelectItem value="approve" className="rounded-lg">
                      Auto-approve
                    </SelectItem>
                    <SelectItem value="reject" className="rounded-lg">
                      Auto-reject
                    </SelectItem>
                    <SelectItem value="escalate" className="rounded-lg">
                      Keep escalating
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
        </div>
      )}

      {/* Preview Badge */}
      <div className="p-3 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
        <div className="flex items-center gap-2 text-blue-700 dark:text-blue-400">
          <UserCheck className="h-4 w-4" />
          <span className="text-sm font-medium">Review Step Preview</span>
        </div>
        <p className="text-xs text-blue-600 dark:text-blue-300 mt-1">
          Workflow will pause here until {config.assigneeType === 'workflow_owner' ? 'the workflow owner' : 'the assigned reviewer'} takes action.
        </p>
        <div className="flex flex-wrap gap-1.5 mt-2">
          {config.allowedActions.map((action) => (
            <Badge key={action} className={`text-xs ${actionColors[action]}`}>
              {actionLabels[action]}
            </Badge>
          ))}
        </div>
      </div>
    </div>
  )
}

export { defaultConfig as defaultHumanReviewNodeConfig }
